import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../../api/client';
import { ForeignAmounts } from '../../components/ForeignAmounts';
import { AppIcon } from '../../components/AppIcon';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import type { RevenueBySourceReport } from '../../types/models';
import { formatDate, formatMoney } from '../../utils/format';
import { sources } from '../masters/sources';

const COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#64748b'];
const compactInr = (v: number) =>
  v >= 1e7
    ? `₹${(v / 1e7).toFixed(1)}Cr`
    : v >= 1e5
      ? `₹${(v / 1e5).toFixed(1)}L`
      : v >= 1e3
        ? `₹${(v / 1e3).toFixed(0)}k`
        : `₹${v}`;

/** Revenue by source (spec §39): App | AdMob | AdX | Google Play | Other | Total, in INR. */
export function RevenueReportPage() {
  const [range, setRange] = useDateFilter('thisMonth');
  const [appId, setAppId] = useState('');
  const query = useQuery({
    queryKey: ['revenue', 'report', range.startDate, range.endDate, appId],
    queryFn: () =>
      api.get<RevenueBySourceReport>('/reports/revenue', {
        startDate: range.startDate,
        endDate: range.endDate,
        appId,
      }),
    placeholderData: keepPreviousData,
  });
  const r = query.data;
  const chart = (r?.sources ?? []).map((s, i) => ({
    name: s.name,
    value: Number(r?.totals[s.id] ?? 0),
    color: COLORS[i % COLORS.length]!,
  }));

  return (
    <>
      <PageHeader
        title="Revenue by source"
        description={`${formatDate(range.startDate)} – ${formatDate(range.endDate)} · all amounts in INR at the rate saved with each entry`}
        actions={
          <>
            <SearchableSelect
              ariaLabel="App"
              className="w-56"
              placeholder="All apps"
              clearable
              value={appId}
              remote={sources.app({})}
              onChange={setAppId}
            />
            <ExportButton
              report="revenue-report"
              params={{ startDate: range.startDate, endDate: range.endDate, appId }}
            />
            <DateRangeFilter value={range} onChange={setRange} />
          </>
        }
      />
      {!r ? (
        <Spinner />
      ) : r.rows.length === 0 ? (
        <Card>
          <EmptyState title="No revenue in this period" />
        </Card>
      ) : (
        <div className="space-y-6">
          <Card bodyClassName="p-0">
            <div className="relative overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">App</th>
                    {r.sources.map((s) => (
                      <th key={s.id} className="px-4 py-3 text-right">
                        {s.name}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-slate-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {r.rows.map((row) => (
                    <tr key={row.app.id}>
                      <td className="px-4 py-3">
                        <Link
                          to={`/apps/${row.app.id}`}
                          className="flex items-center gap-2 font-medium text-slate-900 hover:text-brand-700"
                        >
                          <AppIcon src={row.app.iconUrl} name={row.app.name} size={24} />
                          {row.app.name}
                        </Link>
                      </td>
                      {r.sources.map((s) => (
                        <td
                          key={s.id}
                          className={`num px-4 py-3 text-right ${Number(row.bySource[s.id]) === 0 ? 'text-slate-300' : ''}`}
                        >
                          {formatMoney(row.bySource[s.id])}
                        </td>
                      ))}
                      <td className="num px-4 py-3 text-right font-semibold text-slate-900">
                        {formatMoney(row.total)}
                        <ForeignAmounts items={row.foreign} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    {r.sources.map((s) => (
                      <td key={s.id} className="num px-4 py-3 text-right">
                        {formatMoney(r.totals[s.id])}
                        <ForeignAmounts items={r.totalsForeign[s.id]} />
                      </td>
                    ))}
                    <td className="num px-4 py-3 text-right text-emerald-700">
                      {formatMoney(r.grandTotal)}
                      <ForeignAmounts items={r.grandForeign} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
          <Card title="Revenue by source">
            <div className="h-64" role="img" aria-label="Revenue by source chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={compactInr}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                  />
                  <Tooltip formatter={(v) => formatMoney(String(v ?? 0))} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {chart.map((c) => (
                      <Cell key={c.name} fill={c.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
